import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  getCurrentUserToken,
  registerWithEmail,
  signInWithEmail,
  signInWithGoogle,
  signOutUser,
  subscribeToAuthChanges,
} from '../lib/firebase';
import {
  fetchCustomerProfile,
  syncCustomerProfile,
  updateCustomerProfile,
} from '../lib/customerApi';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [initializing, setInitializing] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);

  const loadProfile = useCallback(async (firebaseUser) => {
    if (!firebaseUser) {
      setProfile(null);
      return null;
    }

    setProfileLoading(true);
    try {
      const token = await firebaseUser.getIdToken();
      let nextProfile = null;

      try {
        nextProfile = await fetchCustomerProfile(token);
      } catch {
        nextProfile = await syncCustomerProfile(token, {
          email: firebaseUser.email,
          first_name: firebaseUser.displayName?.split(' ')?.[0] || '',
          last_name: firebaseUser.displayName?.split(' ')?.slice(1).join(' ') || '',
        });
      }

      setProfile(nextProfile);
      return nextProfile;
    } catch (error) {
      console.warn('Unable to load customer profile:', error.message);
      setProfile(null);
      return null;
    } finally {
      setProfileLoading(false);
    }
  }, []);

  useEffect(() => {
    let unsubscribe = () => {};

    subscribeToAuthChanges((firebaseUser) => {
      setUser(firebaseUser);
      loadProfile(firebaseUser).finally(() => setInitializing(false));
    })
      .then((unsub) => {
        unsubscribe = unsub;
      })
      .catch(() => setInitializing(false));

    return () => unsubscribe();
  }, [loadProfile]);

  const getToken = useCallback(async () => getCurrentUserToken(), []);

  const loginWithEmail = useCallback(async (email, password) => {
    const credential = await signInWithEmail(email, password);
    await loadProfile(credential.user);
    return credential.user;
  }, [loadProfile]);

  const createAccountWithEmail = useCallback(async (email, password, profilePayload = {}) => {
    const credential = await registerWithEmail(email, password);
    const token = await credential.user.getIdToken();
    const nextProfile = await syncCustomerProfile(token, {
      email,
      ...profilePayload,
    });
    setProfile(nextProfile);
    return credential.user;
  }, []);

  const loginWithGoogle = useCallback(async () => {
    const credential = await signInWithGoogle();
    const token = await credential.user.getIdToken();
    const displayParts = String(credential.user.displayName || '').trim().split(/\s+/);
    const nextProfile = await syncCustomerProfile(token, {
      email: credential.user.email,
      first_name: displayParts[0] || '',
      last_name: displayParts.slice(1).join(' '),
    });
    setProfile(nextProfile);
    return credential.user;
  }, []);

  const logout = useCallback(async () => {
    await signOutUser();
    setUser(null);
    setProfile(null);
  }, []);

  const saveProfile = useCallback(async (payload) => {
    const token = await getCurrentUserToken();
    if (!token) {
      throw new Error('You must be signed in to update your profile.');
    }
    const nextProfile = await updateCustomerProfile(token, payload);
    setProfile(nextProfile);
    return nextProfile;
  }, []);

  const refreshProfile = useCallback(async () => {
    if (!user) return null;
    return loadProfile(user);
  }, [loadProfile, user]);

  const value = useMemo(
    () => ({
      user,
      profile,
      initializing,
      profileLoading,
      isAuthenticated: !!user,
      getToken,
      loginWithEmail,
      createAccountWithEmail,
      loginWithGoogle,
      logout,
      saveProfile,
      refreshProfile,
    }),
    [
      user,
      profile,
      initializing,
      profileLoading,
      getToken,
      loginWithEmail,
      createAccountWithEmail,
      loginWithGoogle,
      logout,
      saveProfile,
      refreshProfile,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
